import type {
  CommendaAiConversationAccessView,
  CommendaAiUploadFile,
} from '@commenda/api/commenda-ai/types';
import type { AgentAttachment } from '@commenda/types';
import { useState, type ReactNode } from 'react';
import { ChatComposer } from './ChatComposer';
import { ChatDropZone } from './ChatDropZone';
import { CommendaAiLoader } from './CommendaAiLoader';
import {
  ComposerClientStrip,
  ComposerControlsHelper,
  ComposerFooterControls,
  type IComposerControlsConfig,
} from './composer-controls';
import { PanelEmptyState } from './PanelEmptyState';
import { PanelHeader } from './PanelHeader';

export interface PanelChatEmptyCopy {
  heading: string;
  description: string;
  suggestions: readonly string[];
}

/**
 * A Commenda AI chat that lives in a side panel.
 *
 * The whole surface, not a kit of parts: drop zone, header, blank state, composer and the
 * conversation itself. Every panel in the product mounts this one component, so none of them can
 * drift in background, spacing or type. The conversation is `CommendaAiLoader`, the same component
 * the full page mounts, so the panel and the page cannot drift either.
 *
 * Hosts differ only in what they hand in: the enterprise panel supplies page context and an expand
 * action, the workflow builder supplies a version badge and the send that saves its canvas first.
 */
export function PanelChat({
  conversationId,
  emptyCopy,
  onStartConversation,
  headerBadge,
  headerTrailing,
  contextPill,
  conversationContextPill,
  composerControls,
  getPageContext,
  beforeSend,
  buildSendBody,
  onGeneratingChange,
  afterDeleteHref,
  renderHeaderTitle,
}: {
  conversationId: string | null;
  emptyCopy: PanelChatEmptyCopy;
  /** Starts the thread from the blank state. The host owns creation because only it knows what has
   * to settle first: the builder saves its canvas and resolves a target version. */
  onStartConversation: (
    text: string,
    attachments: AgentAttachment[]
  ) => Promise<unknown>;
  headerBadge?: ReactNode;
  /** A function rather than a node: the conversation's sharing state is only known once the loader
   * has read it, and the enterprise panel's visibility control needs it. */
  headerTrailing?: (access?: CommendaAiConversationAccessView) => ReactNode;
  /** Above the composer on the blank state. */
  contextPill?: ReactNode;
  /** Above the composer once a conversation exists; defaults to `contextPill`. */
  conversationContextPill?: ReactNode;
  /** The client, agent, mode and model pickers this panel offers, on the blank state and in the
   * conversation alike. */
  composerControls?: IComposerControlsConfig;
  getPageContext?: React.ComponentProps<
    typeof CommendaAiLoader
  >['getPageContext'];
  beforeSend?: React.ComponentProps<typeof CommendaAiLoader>['beforeSend'];
  buildSendBody?: React.ComponentProps<
    typeof CommendaAiLoader
  >['buildSendBody'];
  onGeneratingChange?: (isGenerating: boolean) => void;
  afterDeleteHref?: string;
  /** The header title once a conversation exists. Defaults to the conversation's own title. */
  renderHeaderTitle?: (title: string) => string;
}) {
  // Handed to the composer, which owns attachment state and the upload. Cleared once taken so the
  // same drop is never uploaded twice.
  const [droppedFiles, setDroppedFiles] = useState<
    CommendaAiUploadFile[] | null
  >(null);

  const header = (title: string, access?: CommendaAiConversationAccessView) => (
    <PanelHeader
      title={renderHeaderTitle ? renderHeaderTitle(title) : title}
      badge={headerBadge}
      trailing={headerTrailing?.(access)}
    />
  );

  if (!conversationId)
    return (
      <ChatDropZone
        className="flex h-full min-w-0 flex-col"
        onFilesDropped={setDroppedFiles}
      >
        {header('New chat')}
        <PanelEmptyState
          heading={emptyCopy.heading}
          description={emptyCopy.description}
          suggestions={emptyCopy.suggestions}
          // The host has already told the user when creation fails.
          onSuggestionClick={(text) =>
            onStartConversation(text, []).catch(() => undefined)
          }
        />
        <div className="flex shrink-0 flex-col gap-sm px-lg pb-lg">
          <ComposerClientStrip composerControls={composerControls} />
          <ChatComposer
            placeholder="Describe your task or ask a question"
            contextSlot={contextPill}
            onSubmit={onStartConversation}
            showDisclaimer={false}
            toolsSlot={
              composerControls &&
              ComposerControlsHelper.hasFooterControls(composerControls) ? (
                <ComposerFooterControls composerControls={composerControls} />
              ) : undefined
            }
            filesToIngest={droppedFiles}
            onFilesIngested={() => setDroppedFiles(null)}
          />
        </div>
      </ChatDropZone>
    );

  return (
    <div className="flex h-full min-w-0 flex-col">
      {/* Keyed so selecting another conversation rebuilds the loader rather than reusing hydrated
          state from the previous one. */}
      <CommendaAiLoader
        key={conversationId}
        conversationId={conversationId}
        layout="panel"
        renderHeader={header}
        contextSlot={conversationContextPill ?? contextPill}
        composerControls={composerControls}
        {...(getPageContext ? { getPageContext } : {})}
        {...(beforeSend ? { beforeSend } : {})}
        {...(buildSendBody ? { buildSendBody } : {})}
        {...(onGeneratingChange ? { onGeneratingChange } : {})}
        {...(afterDeleteHref ? { afterDeleteHref } : {})}
      />
    </div>
  );
}
